import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Loader2, Plus } from 'lucide-react';
import { useCatalogSearch, CatalogSearchResult } from '../hooks/useCatalogSearch';
import { useCreateProduct } from '../hooks/useProducts';

export default function CatalogSearch() {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { data, isFetching } = useCatalogSearch(query);
  const results = data?.results;
  const createProduct = useCreateProduct();

  // Close dropdown on click outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Open dropdown when results arrive
  useEffect(() => {
    if (results && results.length > 0 && query.trim().length >= 2) {
      setIsOpen(true);
    }
  }, [results, query]);

  function handleTrack(result: CatalogSearchResult) {
    createProduct.mutate(
      {
        name: result.name || 'Untitled Product',
        normalized_url: result.normalized_url,
      },
      {
        onSuccess: () => {
          setQuery('');
          setIsOpen(false);
        },
      }
    );
  }

  function formatPrice(price: number | null, currency: string | null) {
    if (price == null) return null;
    const sym = currency === 'USD' || !currency ? '$' : currency + ' ';
    return `${sym}${price.toFixed(2)}`;
  }

  const showDropdown = isOpen && query.trim().length >= 2;

  return (
    <div ref={containerRef} className="relative rounded-lg border border-border bg-card p-6 space-y-2">
      <h3 className="text-lg font-medium text-card-foreground">Search Catalog</h3>
      <p className="text-sm text-muted-foreground">
        Find products already in our catalog and start tracking them instantly.
      </p>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && query.trim().length >= 2) {
              setIsOpen(false);
              navigate(`/search?q=${encodeURIComponent(query.trim())}`);
            }
          }}
          onFocus={() => {
            if (results && results.length > 0 && query.trim().length >= 2) {
              setIsOpen(true);
            }
          }}
          placeholder="Search by product name, brand, or retailer..."
          className="flex h-10 w-full rounded-md border border-input bg-background pl-10 pr-10 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        />
        {isFetching && (
          <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
        )}
      </div>

      {showDropdown && (
        <div className="absolute left-0 right-0 top-full z-50 mt-1 max-h-80 overflow-y-auto rounded-md border border-border bg-card shadow-lg">
          {results && results.length > 0 ? (
            <ul className="divide-y divide-border">
              {results.map((result) => (
                <li
                  key={result.catalog_product_id}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-accent/50 transition-colors"
                >
                  {result.image_url ? (
                    <img
                      src={result.image_url}
                      alt=""
                      className="h-10 w-10 rounded object-cover flex-shrink-0 bg-muted"
                    />
                  ) : (
                    <div className="h-10 w-10 rounded bg-muted flex-shrink-0" />
                  )}

                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-card-foreground truncate">
                      {result.name || 'Untitled'}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      {result.retailer && <span>{result.retailer}</span>}
                      {result.retailer && result.current_best_price != null && (
                        <span aria-hidden="true">&middot;</span>
                      )}
                      {result.current_best_price != null && (
                        <span className="font-medium text-foreground">
                          {formatPrice(result.current_best_price, result.currency)}
                        </span>
                      )}
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => handleTrack(result)}
                    disabled={createProduct.isPending}
                    className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground shadow hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 flex-shrink-0"
                  >
                    {createProduct.isPending ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Plus className="h-3 w-3" />
                    )}
                    Track
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <div className="px-4 py-6 text-center text-sm text-muted-foreground">
              No results found
            </div>
          )}
        </div>
      )}
    </div>
  );
}
